import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    FormControl,
    InputLabel,
    OutlinedInput,
    Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useDispatch, useSelector } from 'react-redux';
import {
    apiGetEexUserLimits,
    apiSaveEexUserLimits
} from '../../../store/feature/eex/eexSlice';

const initialLimits = {
    deal_order_limit_eur: 0,
    deal_order_limit_chf: 0,
    deal_order_limit_mw: 0,
    daily_buy_limit_eur: 0,
    daily_buy_limit_chf: 0,
    daily_buy_limit_mw: 0,
    daily_sell_limit_eur: 0,
    daily_sell_limit_chf: 0,
    daily_sell_limit_mw: 0,
    net_buy_sell_limit_eur: 0,
    net_buy_sell_limit_chf: 0,
    net_buy_sell_limit_mw: 0
};

export default function EexUserLimits() {
    const dispatch = useDispatch();
    const { isGetEexUserLimitsLoading, isSaveEexUserLimitsLoading } =
        useSelector((state) => state.eex);

    const [state, setState] = useState(initialLimits);
    const [statusMessage, setStatusMessage] = useState('');
    const [statusType, setStatusType] = useState('');

    useEffect(() => {
        (async () => {
            const resp = await dispatch(apiGetEexUserLimits());
            if (apiGetEexUserLimits.fulfilled.match(resp)) {
                const data = resp.payload?.data || {};
                setState({
                    deal_order_limit_eur: data.deal_order_limit_eur || 0,
                    deal_order_limit_chf: data.deal_order_limit_chf || 0,
                    deal_order_limit_mw: data.deal_order_limit_mw || 0,
                    daily_buy_limit_eur: data.daily_buy_limit_eur || 0,
                    daily_buy_limit_chf: data.daily_buy_limit_chf || 0,
                    daily_buy_limit_mw: data.daily_buy_limit_mw || 0,
                    daily_sell_limit_eur: data.daily_sell_limit_eur || 0,
                    daily_sell_limit_chf: data.daily_sell_limit_chf || 0,
                    daily_sell_limit_mw: data.daily_sell_limit_mw || 0,
                    net_buy_sell_limit_eur: data.net_buy_sell_limit_eur || 0,
                    net_buy_sell_limit_chf: data.net_buy_sell_limit_chf || 0,
                    net_buy_sell_limit_mw: data.net_buy_sell_limit_mw || 0
                });
            }
        })();
    }, [dispatch]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setState((prev) => ({ ...prev, [name]: value }));
        setStatusMessage('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            deal_order_limit_eur: parseFloat(state.deal_order_limit_eur) || 0,
            deal_order_limit_chf: parseFloat(state.deal_order_limit_chf) || 0,
            deal_order_limit_mw: parseFloat(state.deal_order_limit_mw) || 0,
            daily_buy_limit_eur: parseFloat(state.daily_buy_limit_eur) || 0,
            daily_buy_limit_chf: parseFloat(state.daily_buy_limit_chf) || 0,
            daily_buy_limit_mw: parseFloat(state.daily_buy_limit_mw) || 0,
            daily_sell_limit_eur: parseFloat(state.daily_sell_limit_eur) || 0,
            daily_sell_limit_chf: parseFloat(state.daily_sell_limit_chf) || 0,
            daily_sell_limit_mw: parseFloat(state.daily_sell_limit_mw) || 0,
            net_buy_sell_limit_eur:
                parseFloat(state.net_buy_sell_limit_eur) || 0,
            net_buy_sell_limit_chf:
                parseFloat(state.net_buy_sell_limit_chf) || 0,
            net_buy_sell_limit_mw: parseFloat(state.net_buy_sell_limit_mw) || 0
        };

        const resp = await dispatch(apiSaveEexUserLimits(payload));

        if (apiSaveEexUserLimits.fulfilled.match(resp)) {
            setStatusMessage('Limits saved successfully!');
            setStatusType('success');
        } else {
            setStatusMessage(
                resp.payload?.message ||
                    resp.payload?.error ||
                    'Unable to save limits. Please try again.'
            );
            setStatusType('error');
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                p: 3,
                maxWidth: '100%',
                width: '100%',
                borderRadius: 2
            }}
        >
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        background:
                            'linear-gradient(135deg, #0b9cfd 0%, #3DC3C1 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    EEX User Limits
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                }}
            >
                {/* Deal Limits */}
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            mb: 0.5
                        }}
                    >
                        Deal Limits
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: '0.75rem'
                        }}
                    >
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="dealLimitEurLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Deal Limit in EUR
                            </InputLabel>
                            <OutlinedInput
                                id="dealLimitEurLabel"
                                label="Deal Limit in EUR"
                                name="deal_order_limit_eur"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                onChange={handleChange}
                                value={state.deal_order_limit_eur}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="dealLimitChfLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Deal Limit in CHF
                            </InputLabel>
                            <OutlinedInput
                                id="dealLimitChfLabel"
                                label="Deal Limit in CHF"
                                name="deal_order_limit_chf"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                onChange={handleChange}
                                value={state.deal_order_limit_chf}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="dealLimitMwhLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Deal Limit in MWh/h
                            </InputLabel>
                            <OutlinedInput
                                id="dealLimitMwhLabel"
                                label="Deal Limit in MWh/h"
                                name="deal_order_limit_mw"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                onChange={handleChange}
                                value={state.deal_order_limit_mw}
                            />
                        </FormControl>
                    </Box>
                </Box>

                {/* Daily Buy Limits */}
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            mb: 0.5
                        }}
                    >
                        Daily Buy Limits
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: '0.75rem'
                        }}
                    >
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="dailyBuyLimitEurLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Daily Buy Limit in EUR
                            </InputLabel>
                            <OutlinedInput
                                id="dailyBuyLimitEurLabel"
                                label="Daily Buy Limit in EUR"
                                name="daily_buy_limit_eur"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                onChange={handleChange}
                                value={state.daily_buy_limit_eur}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="dailyBuyLimitChfLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Daily Buy Limit in CHF
                            </InputLabel>
                            <OutlinedInput
                                id="dailyBuyLimitChfLabel"
                                label="Daily Buy Limit in CHF"
                                name="daily_buy_limit_chf"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                onChange={handleChange}
                                value={state.daily_buy_limit_chf}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="dailyBuyLimitMwhLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Daily Buy Limit in MWh/h
                            </InputLabel>
                            <OutlinedInput
                                id="dailyBuyLimitMwhLabel"
                                label="Daily Buy Limit in MWh/h"
                                name="daily_buy_limit_mw"
                                type="number"
                                inputProps={{ min: 0, step: 'any' }}
                                onChange={handleChange}
                                value={state.daily_buy_limit_mw}
                            />
                        </FormControl>
                    </Box>
                </Box>

                {/* Daily Sell Limits */}
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            mb: 0.5
                        }}
                    >
                        Daily Sell Limits
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: '0.75rem'
                        }}
                    >
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                shrink
                                id="dailySellLimitEurLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Daily Sell Limit in EUR
                            </InputLabel>
                            <OutlinedInput
                                id="dailySellLimitEurLabel"
                                label="Daily Sell Limit in EUR"
                                name="daily_sell_limit_eur"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                onChange={handleChange}
                                value={state.daily_sell_limit_eur}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                shrink
                                id="dailySellLimitChfLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Daily Sell Limit in CHF
                            </InputLabel>
                            <OutlinedInput
                                id="dailySellLimitChfLabel"
                                label="Daily Sell Limit in CHF"
                                name="daily_sell_limit_chf"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                onChange={handleChange}
                                value={state.daily_sell_limit_chf}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="dailySellLimitMwhLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Daily Sell Limit in MWh/h
                            </InputLabel>
                            <OutlinedInput
                                id="dailySellLimitMwhLabel"
                                label="Daily Sell Limit in MWh/h"
                                name="daily_sell_limit_mw"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                onChange={handleChange}
                                value={state.daily_sell_limit_mw}
                            />
                        </FormControl>
                    </Box>
                </Box>

                {/* Net Buy Sell Limits */}
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            mb: 0.5
                        }}
                    >
                        Net Buy/Sell Limits
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: '0.75rem'
                        }}
                    >
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="netBuySellLimitEurLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Net Buy/Sell Limit in Eur
                            </InputLabel>
                            <OutlinedInput
                                id="netBuySellLimitEurLabel"
                                label="Net Buy Sell Limit in Eur"
                                name="net_buy_sell_limit_eur"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                onChange={handleChange}
                                value={state.net_buy_sell_limit_eur}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="netBuySellLimitChfLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Net Buy/Sell Limit in CHF
                            </InputLabel>
                            <OutlinedInput
                                id="netBuySellLimitChfLabel"
                                label="Net Buy/Sell Limit in CHF"
                                name="net_buy_sell_limit_chf"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                onChange={handleChange}
                                value={state.net_buy_sell_limit_chf}
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="netBuySellLimitMwhLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Net Buy/Sell Limit in MWh/h
                            </InputLabel>
                            <OutlinedInput
                                id="netBuySellLimitMwhLabel"
                                label="Net Buy Sell Limit in MWh/h"
                                name="net_buy_sell_limit_mw"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                onChange={handleChange}
                                value={state.net_buy_sell_limit_mw}
                            />
                        </FormControl>
                    </Box>
                </Box>
            </Box>

            {statusMessage && (
                <Alert
                    severity={statusType}
                    icon={
                        statusType === 'success' ? (
                            <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                        ) : (
                            <ErrorIcon sx={{ color: '#F44336' }} />
                        )
                    }
                    sx={{
                        mt: 2,
                        backgroundColor:
                            statusType === 'success' ? '#E8F5E9' : '#FFEBEE',
                        color: statusType === 'success' ? '#2E7D32' : '#C62828',
                        border: `1px solid ${
                            statusType === 'success' ? '#C8E6C9' : '#FFCDD2'
                        }`
                    }}
                >
                    {statusMessage}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={
                        isGetEexUserLimitsLoading || isSaveEexUserLimitsLoading
                    }
                    sx={{
                        backgroundColor: '#0b9cfd',
                        color: '#fff',
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        borderRadius: 1,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(11, 156, 253, 0.3)',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(11, 156, 253, 0.4)'
                        },
                        '&:disabled': {
                            background: '#CCCCCC',
                            boxShadow: 'none'
                        },
                        marginLeft: 'auto'
                    }}
                >
                    {isSaveEexUserLimitsLoading ? 'Saving...' : 'Save Limits'}
                </Button>
            </Box>
        </Box>
    );
}
